import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { _ } from '/@/locale';
import { getRecommendationBlockImage } from '/@/store/private-data/recommendation-pdfs/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import type { BlockImageKey } from '/@/views/recommendations/v2/block-image';
import { flowEventProps, useSeedRecommendationFlow } from '/@/views/recommendations/v2/flow-tracking';

/**
 * Feminine nouns, every one -- the messages below agree on "copiada", so the label can't be
 * masculine. Exported so the copy button (`Section`, in `./index.tsx`) can read "Copiar
 * {{block}}" off the same map instead of a second, driftable copy of these strings.
 */
export const BLOCK_LABELS: Record<BlockImageKey, () => string> = {
    density_curve: () => _('Densidad'),
    density_table: () => _('Densidad por ambiente'),
    phenology: () => _('Fenología'),
    nutrients: () => _('Fertilización'),
    density_simulation: () => _('Simulación de densidad'),
};

const canWriteImagesToClipboard = () =>
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function';

/**
 * Fetches one v2 result-page block as a PNG and writes it to the OS clipboard.
 *
 * The image comes from a synchronous backend call (roughly 1-3s, a real Puppeteer render), so
 * `copyingBlock` is exposed for the triggering button to show a busy state. When the browser
 * can't write an image to the clipboard -- older Safari, an insecure context, a denied
 * permission -- this falls back to opening the PNG in a new tab rather than failing silently,
 * per the ticket's acceptance criteria.
 */
export const useCopyBlockImage = (
    recommendation: Recommendation | undefined,
    /** Where the copy button sits, reported with the event. */
    location: 'management' | 'result'
) => {
    const dispatch = useDispatch();
    const flow = useSeedRecommendationFlow();
    const [copyingBlock, setCopyingBlock] = useState<BlockImageKey | undefined>(undefined);

    /**
     * `extraPayload` is merged into the payload the render reads, for a block whose content
     * isn't in the saved recommendation — the density simulation's table is computed in the
     * browser from inputs that are never saved.
     */
    const copyBlock = async (block: BlockImageKey, extraPayload?: Record<string, unknown>) => {
        if (!recommendation || copyingBlock) return;

        const label = BLOCK_LABELS[block]();
        setCopyingBlock(block);
        try {
            const blob: Blob = await dispatch(
                getRecommendationBlockImage({
                    recommendation: extraPayload ? { ...recommendation, ...extraPayload } : recommendation,
                    block,
                })
            );

            const method = canWriteImagesToClipboard() ? 'clipboard' : 'new_tab';
            if (method === 'clipboard') {
                await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                enqueueSnackbar(
                    _('{{block}} copiada como imagen. Ya podés pegarla donde la necesites.', { block: label }),
                    { variant: 'success' }
                );
            } else {
                window.open(URL.createObjectURL(blob), '_blank');
                enqueueSnackbar(
                    _(
                        'Tu navegador no permite copiar {{block}} automáticamente. La abrimos en una pestaña nueva para que la guardes o copies manualmente.',
                        { block: label }
                    ),
                    { variant: 'info' }
                );
            }
            mpTrackEvent('Seed recommendation block copied as image', {
                ...flowEventProps(flow),
                block,
                method,
                location,
            });
        } catch (error) {
            console.error(error);
            mpTrackEvent('Seed recommendation block copy failed', {
                ...flowEventProps(flow),
                block,
                error_detail: error instanceof Error ? error.message : String(error),
            });
            enqueueSnackbar(_('No pudimos copiar {{block}} como imagen. Intentá de nuevo.', { block: label }), {
                variant: 'error',
            });
        } finally {
            setCopyingBlock(undefined);
        }
    };

    return { copyBlock, copyingBlock };
};
